// Iter 74 — Per-vendor Purchase Orders tab on VendorDetail.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { PO_STATUS_BADGE, formatAmount } from "./vendor_constants";

export default function VendorPOsTab({ vendor }) {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        api.get(`/vendors/${vendor.id}/purchase-orders`)
            .then(({ data }) => { if (!cancelled) setRows(data.items || []); })
            .catch((err) => { if (!cancelled) toast.error(formatApiError(err, "Failed to load POs")); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [vendor.id]);

    if (loading) return <div className="text-sm text-slate-500 py-8 text-center" data-testid="vendor-pos-loading">Loading…</div>;

    return (
        <div data-testid="vendor-pos-tab">
            <div className="mb-3">
                <h3 className="font-medium text-slate-900">Purchase Orders</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                    POs derived from this vendor&apos;s accepted quotations. New POs are created from the Quotations tab via &quot;Convert to PO&quot;.
                </p>
            </div>
            {rows.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-200 rounded-lg" data-testid="vendor-pos-empty">
                    <FileText size={28} className="text-slate-300 mx-auto mb-2" />
                    <div className="text-sm text-slate-500">No purchase orders</div>
                    <div className="text-xs text-slate-400 mt-1">Accept a quotation in the Quotations tab and convert it to PO.</div>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="vendor-pos-table">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="text-left font-medium px-4 py-3">Code</th>
                                <th className="text-left font-medium px-4 py-3">From Quote</th>
                                <th className="text-left font-medium px-4 py-3">PO Date</th>
                                <th className="text-right font-medium px-4 py-3">Grand Total</th>
                                <th className="text-right font-medium px-4 py-3">Billed %</th>
                                <th className="text-left font-medium px-4 py-3">Status</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((p) => {
                                const badge = PO_STATUS_BADGE[p.status] || PO_STATUS_BADGE.draft;
                                return (
                                    <tr key={p.id} data-testid={`vendor-po-row-${p.code}`}
                                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                        onClick={() => navigate(`/vendor-purchase-orders/${p.id}`)}>
                                        <td className="px-4 py-3 font-mono text-xs text-slate-700">{p.code}</td>
                                        <td className="px-4 py-3 text-xs text-slate-700 font-mono">{p.vendor_quotation_code}</td>
                                        <td className="px-4 py-3 text-xs text-slate-700">{formatDate(p.po_date)}</td>
                                        <td className="px-4 py-3 text-right font-mono text-slate-900">{formatAmount(p.grand_total, p.currency)}</td>
                                        <td className="px-4 py-3 text-right text-xs text-slate-700 font-mono">{Number(p.billing_progress_pct || 0).toFixed(0)}%</td>
                                        <td className="px-4 py-3">
                                            <Badge className={`${badge.className} hover:${badge.className}`}>{badge.label}</Badge>
                                        </td>
                                        <td className="px-4 py-3 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
